import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import {
  Search, Clock, CheckCircle2, AlertTriangle, Mail, Calendar, Play,
  Send, Eye, Edit3, History, RefreshCw, ChevronDown, ChevronUp, X,
  User, Users, Filter, RotateCcw, GitBranch, FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "../components/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

import IntakeSubmissions from "./IntakeSubmissions";
import ActivationDetailsTab from "../components/activation/ActivationDetailsTab";
import ActivationAgreementTab from "../components/activation/ActivationAgreementTab";
import ActivationChecklistTab from "../components/activation/ActivationChecklistTab";
import ActivationActivateTab from "../components/activation/ActivationActivateTab";
import StructuredEmailEditor from "../components/activation/StructuredEmailEditor";
import { PlanningCallSettingsButton, PlanningCallStatusCell } from "../components/activation/PlanningCallAutomation";

// ============================================
// HELPER FUNCTIONS
// ============================================

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

// ============================================
// SHARED COMPONENTS (from ActivatedProjects)
// ============================================

function EmailStatusBadge({ status }) {
  const config = {
    sent: { label: "Sent", variant: "default", className: "bg-green-100 text-green-800 border-green-200" },
    failed: { label: "Failed", variant: "destructive", className: "bg-red-100 text-red-800 border-red-200" },
    skipped: { label: "Skipped", variant: "secondary", className: "bg-gray-100 text-gray-600" },
    draft_edited: { label: "Edited", variant: "outline", className: "bg-amber-50 text-amber-700 border-amber-200" },
    not_sent: { label: "Not Sent", variant: "outline", className: "bg-gray-50 text-gray-500" }
  };
  
  const c = config[status] || config.not_sent;
  return <Badge variant={c.variant} className={c.className}>{c.label}</Badge>;
}

function EmailPreview({ html, subject, to, cc, sentAt, sentBy }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-muted-foreground w-16">Subject:</span>
          <span className="font-medium">{subject || "No subject"}</span>
        </div>
        <div className="flex items-start gap-2 text-sm">
          <span className="font-medium text-muted-foreground w-16">To:</span>
          <span>{to || "-"}</span>
        </div>
        {cc && cc.length > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <span className="font-medium text-muted-foreground w-16">CC:</span>
            <span>{cc.join(", ")}</span>
          </div>
        )}
        {sentAt && (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-muted-foreground w-16">Sent:</span>
            <span>{formatDateTime(sentAt)}</span>
            {sentBy && <span className="text-muted-foreground">by {sentBy}</span>}
          </div>
        )}
      </div>
      
      <div className="border rounded-lg bg-white">
        <div 
          className="p-6 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: html || "<p>No content</p>" }}
        />
      </div>
    </div>
  );
}

function EmailHistoryPanel({ programId, open, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  
  useEffect(() => {
    if (open && programId) {
      fetchHistory();
    }
  }, [open, programId]);
  
  async function fetchHistory() {
    setLoading(true);
    try {
      const response = await apiClient.get(`/programs/${programId}/kickoff-email-history`);
      setHistory(response.data || []);
    } catch (error) {
      toast.error("Failed to load email history");
    } finally {
      setLoading(false);
    }
  }
  
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Email History
          </SheetTitle>
          <SheetDescription>
            Complete audit trail of welcome emails for this project
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No emails have been sent yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((log) => (
                <Collapsible key={log.id}>
                  <div className="border rounded-lg">
                    <CollapsibleTrigger asChild>
                      <button className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            log.status === "sent" ? "bg-green-500" : 
                            log.status === "failed" ? "bg-red-500" : "bg-gray-400"
                          }`} />
                          <div className="text-left">
                            <p className="font-medium text-sm">
                              {formatDateTime(log.sent_at)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {log.comm_type === "planning_call_reminder"
                                ? `Planning call reminder ${log.reminder_number || ""}${log.send_mode === "test" ? ` (test → ${log.recipient_email})` : ""}`
                                : `${log.is_resend ? "Resent" : "Initial send"} by ${log.sent_by_name || "System"}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <EmailStatusBadge status={log.status} />
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-4 pb-4 pt-0 border-t">
                        <div className="pt-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-muted-foreground">To:</span>
                              <p className="font-medium">{log.recipient_email}</p>
                            </div>
                            {log.cc_recipients && log.cc_recipients.length > 0 && (
                              <div>
                                <span className="text-muted-foreground">CC:</span>
                                <p className="font-medium">
                                  {log.cc_recipients.map(r => r.email).join(", ")}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground">Subject:</span>
                            <p className="font-medium">{log.subject}</p>
                          </div>
                          {log.error_message && (
                            <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                              Error: {log.error_message}
                            </div>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                            className="w-full"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Full Email
                          </Button>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          )}
        </div>
        
        <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Email Snapshot</DialogTitle>
              <DialogDescription>
                This is the exact email that was sent at {selectedLog && formatDateTime(selectedLog.sent_at)}
              </DialogDescription>
            </DialogHeader>
            {selectedLog && (
              <EmailPreview
                html={selectedLog.body_html}
                subject={selectedLog.subject}
                to={selectedLog.recipient_email}
                cc={selectedLog.cc_recipients?.map(r => r.email)}
                sentAt={selectedLog.sent_at}
                sentBy={selectedLog.sent_by_name}
              />
            )}
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

function ResendEmailDialog({ program, open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [sending, setSending] = useState(false);
  const [toRecipient, setToRecipient] = useState({ name: "", email: "", type: "client" });
  const [ccRecipients, setCcRecipients] = useState([]);
  const [showAddCC, setShowAddCC] = useState(false);
  const [newCCName, setNewCCName] = useState("");
  const [newCCEmail, setNewCCEmail] = useState("");
  
  useEffect(() => {
    if (open && program?.id) {
      fetchEmailData();
    }
  }, [open, program?.id]);
  
  async function fetchEmailData() {
    setLoading(true);
    try {
      const response = await apiClient.get(`/programs/${program.id}/kickoff-email-current`);
      setEmailData(response.data);
      
      const recipients = response.data.all_recipients || [];
      const primary = recipients.find(r => r.type === "client") || recipients[0];
      const cc = recipients.filter(r => r !== primary);
      
      if (primary) {
        setToRecipient(primary);
      } else {
        setToRecipient({
          name: response.data.client_name || "",
          email: response.data.client_email || "",
          type: "client"
        });
      }
      setCcRecipients(cc);
    } catch (error) {
      toast.error("Failed to load email data");
    } finally {
      setLoading(false);
    }
  }
  
  function addCCRecipient() {
    if (!newCCEmail.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCCEmail)) {
      toast.error("Invalid email format");
      return;
    }
    if (ccRecipients.some(r => r.email === newCCEmail)) {
      toast.error("This recipient is already added");
      return;
    }
    
    setCcRecipients([...ccRecipients, {
      name: newCCName.trim() || newCCEmail,
      email: newCCEmail.trim().toLowerCase(),
      type: "external"
    }]);
    setNewCCName("");
    setNewCCEmail("");
    setShowAddCC(false);
  }
  
  function removeCCRecipient(email) {
    setCcRecipients(ccRecipients.filter(r => r.email !== email));
  }
  
  async function handleResend() {
    if (!toRecipient.email) {
      toast.error("Primary recipient email is required");
      return;
    }
    
    setSending(true);
    try {
      const response = await apiClient.post(`/programs/${program.id}/resend-kickoff-email`, {
        to_recipient: toRecipient,
        cc_recipients: ccRecipients,
        use_latest_template: true
      });
      
      if (response.data.success) {
        toast.success("Welcome email resent successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error(response.data.error || "Failed to send email");
      }
    } catch (error) {
      toast.error("Failed to resend email");
    } finally {
      setSending(false);
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Resend Welcome Email
          </DialogTitle>
          <DialogDescription>
            {program?.program_name}
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-medium text-sm">Recipients</h3>
              
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="bg-blue-100 text-blue-800">To</Badge>
                  <span className="text-sm font-medium">Primary Recipient</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={toRecipient.name}
                      onChange={(e) => setToRecipient({ ...toRecipient, name: e.target.value })}
                      placeholder="Recipient name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={toRecipient.email}
                      onChange={(e) => setToRecipient({ ...toRecipient, email: e.target.value })}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">CC</Badge>
                    <span className="text-sm text-muted-foreground">Additional Recipients</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowAddCC(true)}>
                    <User className="w-4 h-4 mr-1" />
                    Add CC
                  </Button>
                </div>
                
                {ccRecipients.length > 0 && (
                  <div className="space-y-2">
                    {ccRecipients.map((r, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{r.name}</span>
                          <span className="text-xs text-muted-foreground">{r.email}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeCCRecipient(r.email)} className="h-7 w-7 p-0">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {showAddCC && (
                  <div className="p-3 border rounded-lg space-y-3 bg-muted/20">
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={newCCName} onChange={(e) => setNewCCName(e.target.value)} placeholder="Name" />
                      <Input type="email" value={newCCEmail} onChange={(e) => setNewCCEmail(e.target.value)} placeholder="Email" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={addCCRecipient}>Add</Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowAddCC(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              <h3 className="font-medium text-sm">Email Preview</h3>
              <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                <EmailPreview
                  html={emailData?.body_html}
                  subject={emailData?.subject}
                  to={toRecipient.email}
                  cc={ccRecipients.map(r => r.email)}
                />
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleResend} disabled={sending || loading}>
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Resend Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewEmailDialog({ program, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = useState(null);
  
  useEffect(() => {
    if (open && program?.id) {
      fetchEmailData();
    }
  }, [open, program?.id]);
  
  async function fetchEmailData() {
    setLoading(true);
    try {
      const response = await apiClient.get(`/programs/${program.id}/kickoff-email-current`);
      setEmailData(response.data);
    } catch (error) {
      toast.error("Failed to load email");
    } finally {
      setLoading(false);
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Welcome Email
          </DialogTitle>
          <DialogDescription>
            {program?.program_name}
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : emailData ? (
          <EmailPreview
            html={emailData.body_html}
            subject={emailData.subject}
            to={emailData.all_recipients?.find(r => r.type === "client")?.email || emailData.client_email}
            cc={emailData.all_recipients?.filter(r => r.type !== "client").map(r => r.email)}
            sentAt={emailData.last_sent_at}
          />
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No email data available</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditEmailDialog({ program, open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState(null);
  
  useEffect(() => {
    if (open && program?.id) {
      fetchEmailData();
    }
  }, [open, program?.id]);
  
  async function fetchEmailData() {
    setLoading(true);
    try {
      const response = await apiClient.get(`/programs/${program.id}/kickoff-email-current`);
      setEmailData(response.data);
      
      if (response.data.template) {
        setEmailTemplate(response.data.template);
      } else {
        setEmailTemplate({
          subject: response.data.subject || "",
          greeting: `Hi ${response.data.client_name?.split(" ")[0] || "there"},`,
          intro: "",
          cta_label: "Book Your Production Planning Call",
          cta_url_mode: "dynamic",
          cta_override_url: "",
          body_sections: [],
          checklist_items: [],
          signature: {
            name: "",
            title: "",
            company: "The Render Pros",
            phone: "",
            email: ""
          }
        });
      }
    } catch (error) {
      toast.error("Failed to load email");
    } finally {
      setLoading(false);
    }
  }
  
  async function handleSave() {
    setSaving(true);
    try {
      await apiClient.put(`/programs/${program.id}/kickoff-email-template`, {
        template: emailTemplate,
        recipients: emailData?.all_recipients || []
      });
      toast.success("Email template saved");
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="w-5 h-5" />
            Edit Welcome Email
          </DialogTitle>
          <DialogDescription>
            {program?.program_name}
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : emailTemplate ? (
          <div className="space-y-4">
            <StructuredEmailEditor
              template={emailTemplate}
              onChange={setEmailTemplate}
              kickoffLink={emailData?.kickoff_booking_link || ""}
              variables={{
                client_name: emailData?.client_name?.split(" ")[0] || "",
                project_name: emailData?.program_name || "",
                pm_name: "",
                start_date: "",
                estimated_delivery_date: ""
              }}
            />
          </div>
        ) : null}
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// AWAITING ACTIVATION TAB CONTENT
// ============================================

function AwaitingActivationContent() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  
  const [fetchingAgreement, setFetchingAgreement] = useState(false);
  const [parsingAgreement, setParsingAgreement] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [activating, setActivating] = useState(false);
  
  // Who we invoice. Pre-filled from the selected project's intake when sales
  // captured it; required before activation either way.
  const [accountsPayable, setAccountsPayable] = useState({});
  const [checklist, setChecklist] = useState({
    client_info_verified: false,
    agreement_attached: false,
    project_dates_confirmed: false,
    scope_reviewed: false,
    payment_confirmed: false,
    kickoff_link_configured: false
  });
  
  const [kickoffLink, setKickoffLink] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  // The activation dialog's tabs unmount when you switch away from them, so
  // anything the user picks in the Activate tab has to be held here to survive
  // the round trip to the Check tab. emailTemplate stays null until the server
  // preview seeds it, which is how the tab knows the user has not edited it yet.
  const [skipKickoffEmail, setSkipKickoffEmail] = useState(false);
  const [assignedPMs, setAssignedPMs] = useState([]);
  const [additionalRecipients, setAdditionalRecipients] = useState([]);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    fetchPrograms();
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  async function fetchPrograms() {
    try {
      const response = await apiClient.get("/programs/awaiting-activation");
      setPrograms(response.data);
    } catch (error) {
      toast.error("Failed to load programs");
    } finally {
      setLoading(false);
    }
  }

  function openActivation(program) {
    setSelectedProgram(program);
    setShowActivateDialog(true);
    setActiveTab("details");
    setParsedData(null);
    // Pre-fill from intake so the common case is nothing to type; falls back to
    // empty for the projects that predate the field.
    setAccountsPayable({ ...(program.accounts_payable || {}) });
    setChecklist({
      client_info_verified: false,
      agreement_attached: program.agreement_stored || false,
      project_dates_confirmed: false,
      scope_reviewed: false,
      payment_confirmed: false,
      kickoff_link_configured: false
    });
    setKickoffLink("");
    setCustomMessage("");
    // Held across tab switches, so it must be cleared per project — otherwise
    // one activation's choices would carry into the next project opened.
    setSkipKickoffEmail(false);
    setAssignedPMs([]);
    setAdditionalRecipients([]);
    setEmailTemplate(null);
  }

  function closeActivation() {
    setSelectedProgram(null);
    setShowActivateDialog(false);
  }

  async function handleFetchAgreement() {
    if (!selectedProgram) return;
    setFetchingAgreement(true);
    
    try {
      await apiClient.post(`/programs/${selectedProgram.id}/fetch-agreement`);
      toast.success("Agreement fetched and stored");
      setChecklist({ ...checklist, agreement_attached: true });
      
      const updatedPrograms = await apiClient.get("/programs/awaiting-activation");
      setPrograms(updatedPrograms.data);
      setSelectedProgram(updatedPrograms.data.find(p => p.id === selectedProgram.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch agreement");
    } finally {
      setFetchingAgreement(false);
    }
  }

  async function handleParseAgreement() {
    if (!selectedProgram) return;
    setParsingAgreement(true);
    
    try {
      const response = await apiClient.post(`/programs/${selectedProgram.id}/parse-agreement`);
      if (response.data.success) {
        setParsedData(response.data);
        toast.success("Agreement parsed successfully");
      } else {
        toast.error(response.data.error_message || "Parsing failed");
        setParsedData(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse agreement");
    } finally {
      setParsingAgreement(false);
    }
  }

  async function handleActivate(activationData) {
    if (!selectedProgram) return;
    
    const payload = activationData || {
      ...checklist,
      kickoff_booking_link: kickoffLink.trim(),
      custom_email_message: customMessage.trim() || null
    };
    
    const allChecked = Object.values({
      client_info_verified: payload.client_info_verified,
      agreement_attached: payload.agreement_attached,
      project_dates_confirmed: payload.project_dates_confirmed,
      scope_reviewed: payload.scope_reviewed,
      payment_confirmed: payload.payment_confirmed,
      kickoff_link_configured: payload.kickoff_link_configured
    }).every(v => v);
    
    if (!allChecked) {
      toast.error("Please complete all checklist items");
      return;
    }
    if (!payload.kickoff_booking_link?.trim()) {
      toast.error("Planning call booking link is required");
      return;
    }

    // Every payment notice routes to this address, so it is a hard gate — the
    // backend rejects activation without it and would surface a raw 400.
    const apName = (accountsPayable?.name || "").trim();
    const apEmail = (accountsPayable?.email || "").trim();
    if (!apName || !apEmail) {
      toast.error("An Accounts Payable Manager (name and email) is required");
      return;
    }
    payload.accounts_payable = {
      name: apName,
      email: apEmail.toLowerCase(),
      title: (accountsPayable?.title || "").trim() || null,
      phone: (accountsPayable?.phone || "").trim() || null,
    };
    
    setActivating(true);
    
    try {
      const response = await apiClient.post(`/programs/${selectedProgram.id}/activate`, payload);
      
      const milestonesCreated = response.data.milestones_created || 0;
      const recipientCount = response.data.recipients_count || 1;
      
      if (response.data.kickoff_email_status === "sent") {
        toast.success("Program activated!", {
          description: `Welcome email sent to ${recipientCount} recipient(s). ${milestonesCreated} milestones created.`
        });
      } else {
        toast.success("Program activated!", {
          description: `${milestonesCreated} milestones created. Email: ${response.data.kickoff_email_error || "skipped"}`
        });
      }
      
      const programId = selectedProgram.id;
      closeActivation();
      
      if (milestonesCreated > 0) {
        navigate(`/programs/${programId}`);
      } else {
        navigate(`/programs/${programId}/confirm-scope`);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate program");
    } finally {
      setActivating(false);
    }
  }

  const filteredPrograms = programs.filter(p => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return p.program_name?.toLowerCase().includes(searchLower) ||
           p.client_name?.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[40vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const ActivationDialogContent = () => (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2 sm:mt-4">
      <TabsList className="grid grid-cols-4 w-full">
        <TabsTrigger value="details" className="text-xs sm:text-sm">Details</TabsTrigger>
        <TabsTrigger value="agreement" className="text-xs sm:text-sm">Agreement</TabsTrigger>
        <TabsTrigger value="checklist" className="text-xs sm:text-sm">Check</TabsTrigger>
        <TabsTrigger value="activate" className="text-xs sm:text-sm">Activate</TabsTrigger>
      </TabsList>

      <TabsContent value="details">
        <ActivationDetailsTab program={selectedProgram} />
      </TabsContent>

      <TabsContent value="agreement">
        <ActivationAgreementTab 
          program={selectedProgram}
          fetchingAgreement={fetchingAgreement}
          parsingAgreement={parsingAgreement}
          parsedData={parsedData}
          onFetchAgreement={handleFetchAgreement}
          onParseAgreement={handleParseAgreement}
        />
      </TabsContent>

      <TabsContent value="checklist">
        <ActivationChecklistTab
          checklist={checklist}
          setChecklist={setChecklist}
          accountsPayable={accountsPayable}
          setAccountsPayable={setAccountsPayable}
          apFromIntake={!!selectedProgram?.accounts_payable?.email}
        />
      </TabsContent>

      <TabsContent value="activate">
        <ActivationActivateTab
          program={selectedProgram}
          checklist={checklist}
          setChecklist={setChecklist}
          kickoffLink={kickoffLink}
          setKickoffLink={setKickoffLink}
          customMessage={customMessage}
          setCustomMessage={setCustomMessage}
          skipKickoffEmail={skipKickoffEmail}
          setSkipKickoffEmail={setSkipKickoffEmail}
          assignedPMs={assignedPMs}
          setAssignedPMs={setAssignedPMs}
          additionalRecipients={additionalRecipients}
          setAdditionalRecipients={setAdditionalRecipients}
          emailTemplate={emailTemplate}
          setEmailTemplate={setEmailTemplate}
          onActivate={handleActivate}
          activating={activating}
        />
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="space-y-4">
      {/* Search and Count */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search project or client..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10"
          />
        </div>
        <Badge variant="outline" className="text-sm px-3 py-1.5">
          <Clock className="w-4 h-4 mr-1.5 text-yellow-600" />
          {programs.length} pending
        </Badge>
      </div>

      {/* Programs Grid */}
      {filteredPrograms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
            <h3 className="text-base font-semibold mb-2">All caught up!</h3>
            <p className="text-sm text-muted-foreground">No programs awaiting activation</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredPrograms.map(program => (
            <Card key={program.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2 px-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base truncate">{program.program_name}</CardTitle>
                    <CardDescription className="truncate">{program.client_name}</CardDescription>
                  </div>
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 flex-shrink-0 text-xs">
                    <Clock className="w-3 h-3 mr-1" /> Pending
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 px-4">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Mail className="w-3.5 h-3.5" />
                    <span className="truncate max-w-[140px]">{program.client_email}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{program.start_window ? new Date(program.start_window).toLocaleDateString() : "—"}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {program.agreement_stored ? (
                    <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Agreement
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700 text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" /> No Agreement
                    </Badge>
                  )}
                </div>
              </CardContent>
              <CardFooter className="px-4 pb-4">
                <Button onClick={() => openActivation(program)} className="w-full rounded-full h-9 text-sm">
                  <Play className="w-4 h-4 mr-2" />
                  Review & Activate
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Mobile: Drawer / Desktop: Dialog */}
      {isMobile ? (
        <Drawer open={showActivateDialog} onOpenChange={v => { if (!v) closeActivation(); }}>
          <DrawerContent className="max-h-[90vh] overflow-y-auto px-4 pb-6">
            <DrawerHeader className="px-0">
              <DrawerTitle className="text-lg" style={{ fontFamily: 'var(--font-sans)' }}>
                {selectedProgram?.program_name}
              </DrawerTitle>
              <DrawerDescription>
                Review and activate this program
              </DrawerDescription>
            </DrawerHeader>
            <ActivationDialogContent />
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={showActivateDialog} onOpenChange={v => { if (!v) closeActivation(); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-sans)' }}>
                Activate Program: {selectedProgram?.program_name}
              </DialogTitle>
              <DialogDescription>
                Review details, fetch agreement, and complete the activation checklist
              </DialogDescription>
            </DialogHeader>
            <ActivationDialogContent />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ============================================
// ACTIVE PROJECTS TAB CONTENT
// ============================================

function ActiveProjectsContent() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [emailStatusFilter, setEmailStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showViewEmail, setShowViewEmail] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  
  useEffect(() => {
    fetchPrograms();
  }, [emailStatusFilter, dateFrom, dateTo]);
  
  async function fetchPrograms() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (emailStatusFilter !== "all") params.append("email_status", emailStatusFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const response = await apiClient.get(`/programs/activated?${params.toString()}`);
      setPrograms(response.data || []);
    } catch (error) {
      toast.error("Failed to load activated projects");
    } finally {
      setLoading(false);
    }
  }
  
  const filteredPrograms = useMemo(() => {
    if (!search.trim()) return programs;
    const searchLower = search.toLowerCase();
    return programs.filter(p => 
      p.program_name?.toLowerCase().includes(searchLower) ||
      p.client_name?.toLowerCase().includes(searchLower)
    );
  }, [programs, search]);
  
  function clearFilters() {
    setSearch("");
    setEmailStatusFilter("all");
    setDateFrom("");
    setDateTo("");
  }
  
  const hasActiveFilters = emailStatusFilter !== "all" || dateFrom || dateTo || search;

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by project or client name..."
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Popover open={showFilters} onOpenChange={setShowFilters}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Filter className="w-4 h-4" />
                    Filters
                    {hasActiveFilters && (
                      <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                        {[emailStatusFilter !== "all", dateFrom, dateTo].filter(Boolean).length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Filters</h4>
                      {hasActiveFilters && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs">
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Clear
                        </Button>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs">Email Status</Label>
                      <Select value={emailStatusFilter} onValueChange={setEmailStatusFilter}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="failed">Failed</SelectItem>
                          <SelectItem value="draft_edited">Edited</SelectItem>
                          <SelectItem value="not_sent">Not Sent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label className="text-xs">From Date</Label>
                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">To Date</Label>
                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              
              <Button variant="outline" onClick={fetchPrograms} size="icon">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredPrograms.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="font-medium">No activated projects found</p>
              <p className="text-sm mt-1">
                {hasActiveFilters ? "Try adjusting your filters" : "Projects will appear here after activation"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="font-semibold">Activation Date</TableHead>
                    <TableHead className="font-semibold">Project Name</TableHead>
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold">Email Status</TableHead>
                    <TableHead className="font-semibold">Last Sent</TableHead>
                    <TableHead className="font-semibold">Planning Call</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPrograms.map((program) => (
                    <TableRow key={program.id} className="hover:bg-muted/20">
                      <TableCell className="font-medium">
                        {formatDate(program.activated_at)}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{program.program_name}</div>
                      </TableCell>
                      <TableCell>
                        <div>{program.client_name}</div>
                        <div className="text-xs text-muted-foreground">{program.client_email}</div>
                      </TableCell>
                      <TableCell>
                        <EmailStatusBadge status={program.kickoff_email_status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {program.last_email_sent_at ? formatDateTime(program.last_email_sent_at) : "-"}
                      </TableCell>
                      <TableCell>
                        <PlanningCallStatusCell
                          program={program}
                          onChange={(fields) =>
                            setPrograms((ps) => ps.map((p) => (p.id === program.id ? { ...p, ...fields } : p)))
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              Actions
                              <ChevronDown className="w-4 h-4 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setSelectedProgram(program); setShowViewEmail(true); }}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Welcome Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setSelectedProgram(program); setShowEditEmail(true); }}>
                              <Edit3 className="w-4 h-4 mr-2" />
                              Edit Welcome Email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { setSelectedProgram(program); setShowResendEmail(true); }}>
                              <Send className="w-4 h-4 mr-2" />
                              Resend Welcome Email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { setSelectedProgram(program); setShowEmailHistory(true); }}>
                              <History className="w-4 h-4 mr-2" />
                              View Email History
                              {program.email_send_count > 1 && (
                                <Badge variant="secondary" className="ml-auto">
                                  {program.email_send_count}
                                </Badge>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Summary Stats */}
      {!loading && filteredPrograms.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-2xl font-light">{filteredPrograms.length}</div>
            <div className="text-sm text-muted-foreground">Total Projects</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-light text-green-600">
              {filteredPrograms.filter(p => p.kickoff_email_status === "sent").length}
            </div>
            <div className="text-sm text-muted-foreground">Emails Sent</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-light text-red-600">
              {filteredPrograms.filter(p => p.kickoff_email_status === "failed").length}
            </div>
            <div className="text-sm text-muted-foreground">Failed</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-light text-amber-600">
              {filteredPrograms.filter(p => p.email_send_count > 1).length}
            </div>
            <div className="text-sm text-muted-foreground">Resent</div>
          </Card>
        </div>
      )}
      
      {/* Dialogs */}
      <ViewEmailDialog
        program={selectedProgram}
        open={showViewEmail}
        onClose={() => setShowViewEmail(false)}
      />
      
      <EditEmailDialog
        program={selectedProgram}
        open={showEditEmail}
        onClose={() => setShowEditEmail(false)}
        onSuccess={fetchPrograms}
      />
      
      <ResendEmailDialog
        program={selectedProgram}
        open={showResendEmail}
        onClose={() => setShowResendEmail(false)}
        onSuccess={fetchPrograms}
      />
      
      <EmailHistoryPanel
        programId={selectedProgram?.id}
        open={showEmailHistory}
        onClose={() => setShowEmailHistory(false)}
      />
    </div>
  );
}

// ============================================
// MAIN PIPELINE COMPONENT
// ============================================

export default function Pipeline() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "active";
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  // Sync tab state with URL changes
  useEffect(() => {
    const urlTab = searchParams.get("tab") || "active";
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams]);
  
  // Update URL when tab changes
  function handleTabChange(value) {
    setActiveTab(value);
    setSearchParams({ tab: value });
  }

  return (
    <div data-testid="pipeline" className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            New Projects
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage project activation flow from intake to execution
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PlanningCallSettingsButton />
          <GitBranch className="w-8 h-8 text-muted-foreground/30 hidden sm:block" />
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3 mb-6">
          <TabsTrigger
            value="active"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Active
          </TabsTrigger>
          <TabsTrigger
            value="awaiting"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <Clock className="w-4 h-4 mr-2" />
            Awaiting Activation
          </TabsTrigger>
          <TabsTrigger
            value="submissions"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <FileText className="w-4 h-4 mr-2" />
            My Submissions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-0">
          <ActiveProjectsContent />
        </TabsContent>

        <TabsContent value="awaiting" className="mt-0">
          <AwaitingActivationContent />
        </TabsContent>

        <TabsContent value="submissions" className="mt-0">
          <IntakeSubmissions />
        </TabsContent>
      </Tabs>
    </div>
  );
}
